// src/components/character_editor/CharacterEditor.jsx
// Полностью на Redux (characterDraft). Локального useState для editedCharacter нет.
// beginEdit(character) при монтировании, patch при правках, save при сохранении.

import React, { useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  IconButton,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Container,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Build as BuildIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

import RaceEditSection from './sections/RaceEditSection';
import BasicInfoEditSection from './sections/BasicInfoEditSection';
import TemplateEditSection from './sections/TemplateEditSection';
import SkillsEditSection from './sections/SkillsEditSection';
import AppearanceEditSection from './sections/AppearanceEditSection';

import { useCharacterDraft } from '../../hooks/useCharacterDraft';
import { clearSubmitError } from '../../store/slices/characterDraftSlice';
import { isLegendEmpty } from '../../utils/characterDraft';

const CharacterEditor = ({
  character,
  races = [],
  skills = [],
  templates = [],
  onSave,
  onCancel,
  onBack,
}) => {
  const dispatch = useDispatch();

  const {
    draft,
    validationErrors,
    submitError,
    isDirty,
    beginEdit,
    patch,
    setValidationErrors,
    save,
    revert,
    clearSubmit,
  } = useCharacterDraft();

  // UI-состояние
  const [activeTab, setActiveTab] = React.useState(0);
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState('');
  const [errorAlert, setErrorAlert] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  // Инициализация редактора персонажем
  useEffect(() => {
    if (character) beginEdit(character);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character]);

  // Показывать submitError из Redux
  useEffect(() => {
    if (submitError) {
      setErrorAlert(submitError);
      clearSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const selectedRace = React.useMemo(() => {
    if (!draft) return null;
    if (draft.race && typeof draft.race === 'object') return draft.race;
    const id = draft.race_id;
    if (!id) return null;
    return races.find((r) => r?.id === id) || null;
  }, [draft, races]);

  const selectedTemplate = React.useMemo(() => {
    if (!draft) return null;
    if (draft.character_template && typeof draft.character_template === 'object') {
      return draft.character_template;
    }
    const id = draft.template_id;
    if (!id) return null;
    return templates.find((t) => t?.id === id) || null;
  }, [draft, templates]);

  // ---------- Обработчики ----------

  const handleNameChange = (name) => patch({ name: name || '' });

  const handleRaceChange = (raceOrId) => {
    if (raceOrId && typeof raceOrId === 'object') {
      patch({ race: raceOrId, race_id: raceOrId.id });
    } else {
      const found = races.find((r) => r?.id === raceOrId) || null;
      patch({ race: found, race_id: raceOrId });
    }
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find((t) => t?.id === templateId);
    if (!template) return;
    const defaultSkills = Array.isArray(template.primary_skills)
      ? template.primary_skills
      : [];
    const currentSkills = Array.isArray(draft?.skills) ? draft.skills : [];
    // нормализуем: и объекты, и строки → приводим к строкам для сравнения
    const currentIds = currentSkills.map((s) =>
      typeof s === 'string' ? s : s?.id
    );
    const merged = [
      ...currentSkills,
      ...defaultSkills.filter((s) => !currentIds.includes(s)),
    ];
    patch({
      character_template: template,
      template_id: template.id,
      skills: merged,
    });
  };

  const handleTemperamentChange = (temperament) =>
    patch({ temperament: temperament || '' });

  const handleSkillsChange = (newSkills) => {
    const normalized = Array.isArray(newSkills)
      ? newSkills.map((s) => (typeof s === 'string' ? s : s?.id || s))
      : [];
    patch({ skills: normalized });
  };

  const handleBackgroundChange = (background) => patch({ background });

  const handleAppearanceChange = (appearance) =>
    patch({
      appearance: { ...(draft?.appearance || {}), ...(appearance || {}) },
    });

  // ---------- Сохранение ----------

  const handleSave = async () => {
    if (!draft?.id) {
      setErrorAlert('Нет id персонажа');
      return;
    }
    setSaving(true);
    try {
      const updated = await save();
      if (updated) {
        setSuccessAlert('Изменения успешно сохранены!');
        setSaveDialogOpen(false);
        if (onSave) onSave(updated);
      } else {
        setErrorAlert('Не удалось сохранить изменения');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    revert();
    setCancelDialogOpen(false);
    if (onCancel) onCancel();
  };

  // ---------- Вкладки ----------

  const tabs = [
    {
      label: 'Основное',
      icon: <PersonIcon />,
      component: (
        <BasicInfoEditSection
          character={draft}
          onNameChange={handleNameChange}
          onBackgroundChange={handleBackgroundChange}
        />
      ),
    },
    {
      label: 'Раса',
      icon: <GroupIcon />,
      component: (
        <RaceEditSection
          character={draft}
          races={races}
          selectedRace={selectedRace}
          onRaceChange={handleRaceChange}
        />
      ),
    },
    {
      label: 'Профессия',
      icon: <BuildIcon />,
      component: (
        <TemplateEditSection
          character={draft}
          templates={templates}
          selectedTemplate={selectedTemplate}
          selectedRace={selectedRace}
          onTemplateChange={handleTemplateChange}
          onTemperamentChange={handleTemperamentChange}
        />
      ),
    },
    {
      label: 'Навыки',
      icon: <BuildIcon />,
      component: (
        <SkillsEditSection
          character={draft}
          skills={skills}
          selectedTemplate={selectedTemplate}
          selectedRace={selectedRace}
          onSkillsChange={handleSkillsChange}
        />
      ),
    },
    {
      label: 'Внешность',
      icon: <PsychologyIcon />,
      component: (
        <AppearanceEditSection
          character={draft}
          onAppearanceChange={handleAppearanceChange}
        />
      ),
    },
  ];

  if (!draft) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Загрузка редактора персонажа...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ bgcolor: 'secondary.main', mr: 2, width: 40, height: 40 }}>
            <PersonIcon />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">Редактор персонажа</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {draft?.name || 'Безымянный персонаж'} • Уровень {draft?.level || 1}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              color="inherit"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
              disabled={!isDirty}
            >
              Отмена
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SaveIcon />}
              onClick={() => setSaveDialogOpen(true)}
              disabled={!isDirty || saving}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h6" gutterBottom align="center">Текущий персонаж</Typography>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 1, bgcolor: 'primary.main' }}>
                    <PersonIcon fontSize="large" />
                  </Avatar>
                  <Typography variant="h6">{draft?.name || 'Безымянный'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRace?.name || 'Раса не выбрана'}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Профессия:</Typography>
                  <Chip label={selectedTemplate?.name || 'Не выбрана'} color="primary" size="small" variant="outlined" />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Темперамент:</Typography>
                  <Chip label={draft?.temperament || 'Не выбран'} color="secondary" size="small" variant="outlined" />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Навыки:</Typography>
                  <Typography variant="body2">{draft?.skills?.length || 0} выбрано</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Предыстория:</Typography>
                  <Typography variant="body2" color={isLegendEmpty(draft?.background) ? 'error' : 'text.primary'}>
                    {isLegendEmpty(draft?.background) ? 'Не заполнена' : 'Заполнена'}
                  </Typography>
                </Box>
                {isDirty && (
                  <Alert severity="info" sx={{ mt: 2 }}>Есть несохраненные изменения</Alert>
                )}
                {validationErrors.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {validationErrors.join(', ')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper elevation={2} sx={{ p: 0 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                {tabs.map((tab, index) => (
                  <Tab key={index} icon={tab.icon} label={tab.label} iconPosition="start" />
                ))}
              </Tabs>
              <Box sx={{ p: 3 }}>
                {tabs[activeTab]?.component || <Typography>Раздел не найден</Typography>}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Сохранить изменения?</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите сохранить изменения для персонажа "{draft?.name || 'безымянного персонажа'}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Отменить изменения?</DialogTitle>
        <DialogContent>
          <Typography>Все несохраненные изменения будут потеряны. Вы уверены?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Продолжить редактирование</Button>
          <Button onClick={handleCancel} variant="contained" color="error">Отменить изменения</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!successAlert} autoHideDuration={4000} onClose={() => setSuccessAlert('')}>
        <Alert severity="success" onClose={() => setSuccessAlert('')}>{successAlert}</Alert>
      </Snackbar>
      <Snackbar open={!!errorAlert} autoHideDuration={6000} onClose={() => setErrorAlert('')}>
        <Alert severity="error" onClose={() => setErrorAlert('')}>{errorAlert}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CharacterEditor;
